/* eslint-disable @next/next/no-img-element */
import React from "react";
import { Menu, Button } from "@gonoodle/gn-universe-ui";
import { SearchIcon, MenuIcon, XIcon } from "@heroicons/react/outline";
import { useRouter } from "next/router";

import { useUser } from "../../contexts/user";
import { ExternalLink, Link } from "../../containers";
import { URLS, ROUTE_PREFIX, PROFILE_TYPES } from "../../constants";
import { navigation } from "../../fixtures";

import profileMenuBackground from "../../assets/images/profileMenuBackground.svg";

function getCurrentUserProfile(user) {
  if (user) {
    return user.classrooms.find(
      (profile) => profile.id === user.selectedClassroomId,
    );
  }
  return undefined;
}

export default function AppBar() {
  const router = useRouter();
  const { user, logout } = useUser();
  const profile = getCurrentUserProfile(user);

  return (
    <nav className="bg-black gn-nav" aria-label="Primary Navigation">
      <div className="flex items-center justify-between h-navbar p-sm container">
        {/* Mobile Nav Start */}
        <div className="lg:hidden flex items-center">
          <Menu.Root>
            <Menu.Trigger className="mr-sm p-xs text-white hover:text-mint focus:outline-none">
              {(isOpen) => {
                if (isOpen) {
                  return (
                    <>
                      <span className="sr-only">Close main menu</span>
                      <XIcon className="block h-7 w-7" aria-hidden="true" />
                    </>
                  );
                }
                return (
                  <>
                    <span className="sr-only">Open main menu</span>
                    <MenuIcon className="block h-7 w-7" aria-hidden="true" />
                  </>
                );
              }}
            </Menu.Trigger>

            <Menu.List
              asChild="div"
              className="bg-gray-800 left-0 right-0 top-[72px]"
            >
              <div className="container px-lg pt-md pb-xl space-y-md">
                <ExternalLink
                  className="text-transparent bg-clip-text bg-gradient-to-r from-yellow to-pink p-sm gn-text-lg font-bold"
                  href="https://gonoodlelive.com/"
                  events={[
                    {
                      event: "Link Clicked",
                      properties: {
                        title: "GoNoodle Live!",
                        sourceElement: "nav",
                        sourceName: "nav",
                      },
                    },
                  ]}
                  linkParams={{
                    title: "GoNoodle Live!",
                    sourceElement: "nav",
                    sourceName: "nav",
                  }}
                >
                  GoNoodle Live!
                </ExternalLink>
                {navigation.compactMenu.map((item) => (
                  <Menu.Item
                    asChild={Link}
                    href={item.slug}
                    key={item.slug}
                    className="flex items-center p-sm  gn-text-lg font-bold text-white"
                    events={[
                      {
                        event: "Link Clicked",
                        properties: {
                          title: item.name,
                          sourceElement: "nav",
                          sourceName: "nav",
                        },
                      },
                    ]}
                  >
                    {item.name}
                  </Menu.Item>
                ))}
              </div>
            </Menu.List>
          </Menu.Root>
          <Link
            href="/"
            events={[
              {
                event: "Link Clicked",
                properties: {
                  title: "GoNoodle Logo",
                  sourceElement: "nav",
                  sourceName: "nav",
                },
              },
            ]}
          >
            <svg
              className="transform transition hover:scale-110"
              width="51"
              height="38"
              viewBox="0 0 51 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M47.9584 28.6096L50.1257 32.3308C50.3206 32.6609 50.242 33.0837 49.9307 33.3172C46.0078 36.2594 41.0827 38.0061 35.7306 37.9999C31.5283 37.9954 27.5911 36.9129 24.1994 35.0192C23.8255 34.8101 23.7029 34.3443 23.9144 33.9803L26.0589 30.3031C26.2778 29.9272 26.7726 29.791 27.159 30.0075C29.682 31.4207 32.6114 32.233 35.7409 32.237C39.9215 32.2415 43.7516 30.8 46.7283 28.3993C47.1188 28.0844 47.7065 28.1834 47.9584 28.6096Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.309 0.70096C16.3055 0.298641 15.959 -0.0175083 15.5463 0.000753035C14.3766 0.0532543 13.1962 0.201627 12.0145 0.453291C7.65001 1.38291 3.90279 3.57826 1.07956 6.5674C0.709626 6.95888 0.730717 7.52898 1.12744 7.87138L3.92617 10.6808C4.34626 11.0437 5.02855 10.9981 5.43383 10.5929C7.50238 8.52764 10.1962 7.00739 13.3073 6.34484C14.0928 6.17764 14.8777 6.07093 15.6557 6.02128C16.0262 5.99788 16.3124 5.696 16.309 5.3342V0.70096Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.3991 11.09C19.241 11.09 24.7897 16.4641 24.7897 23.0911C24.7897 29.718 19.241 35.1001 12.3991 35.1001C5.54816 35.1001 0 29.718 0 23.0911C0 16.4641 5.54816 11.09 12.3991 11.09ZM13.3918 28.059C16.24 28.059 18.5482 25.8229 18.5482 23.0646C18.5482 20.3064 16.24 18.0702 13.3918 18.0702C10.5443 18.0702 8.23543 20.3064 8.23543 23.0646C8.23543 25.8229 10.5443 28.059 13.3918 28.059Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M35.5584 0.978394C43.3011 0.978394 49.5797 7.04857 49.5797 14.5348C49.5797 22.021 43.3011 28.1003 35.5584 28.1003C27.8064 28.1003 21.5279 22.021 21.5279 14.5348C21.5279 7.04857 27.8064 0.978394 35.5584 0.978394ZM34.6059 20.4398C37.7798 20.4398 40.3529 17.9518 40.3529 14.8831C40.3529 11.8145 37.7798 9.32646 34.6054 9.32646C31.4315 9.32646 28.8584 11.8145 28.8584 14.8831C28.8584 17.9518 31.4315 20.4398 34.6059 20.4398Z"
                fill="white"
              />
            </svg>
          </Link>
        </div>
        {/* Mobile Nav End */}

        {/* Desktop Nav Start */}
        <Link
          href="/"
          className="hidden lg:block"
          events={[
            {
              event: "Link Clicked",
              properties: {
                title: "GoNoodle Logo",
                sourceElement: "nav",
                sourceName: "nav",
              },
            },
          ]}
        >
          <img
            style={{ width: "150px" }}
            className="transform transition hover:scale-110"
            src="https://cdn.gonoodle.com/Universe-Component-Library/general/GoNoodleLogo_Registered_Reverse.svg"
            alt="GoNoodle logo"
          />
        </Link>

        <div className="hidden lg:flex items-center">
          {navigation.menus.map((list) => {
            const numberOfSections = list.sections.length;
            return (
              <Menu.Root key={list.title}>
                <Menu.Trigger className="text-white border-mint rounded border-2 border-black px-sm py-xs hover:border-mint focus:outline-none">
                  <span className="gn-text-sm font-bold">{list.title}</span>
                </Menu.Trigger>
                <Menu.List
                  key={list.title}
                  className={`${
                    numberOfSections === 1
                      ? "flex flex-col space-y-sm w-screen-xs"
                      : "grid grid-cols-3 gap-x-12"
                  } p-md bg-white shadow-lg rounded top-[64px] mx-sm`}
                  asChild={numberOfSections === 1 ? "ul" : "div"}
                >
                  {list.sections.map((section) => {
                    if (numberOfSections === 1) {
                      return (
                        <React.Fragment key={section.slug}>
                          {section.tags.map((tag) => (
                            <Menu.Item
                              asChild={Link}
                              href={tag.slug}
                              key={tag.slug}
                              className="group bg-white hover:bg-gray-100 p-sm rounded flex items-center space-x-md gn-text"
                              events={[
                                {
                                  event: "Link Clicked",
                                  properties: {
                                    title: tag.name,
                                    sourceElement: "nav",
                                    sourceName: "nav",
                                  },
                                },
                              ]}
                            >
                              {tag.img ? (
                                <img
                                  className="w-xl transform transition group-hover:scale-125 group-hover:-translate-y-xs p-xs"
                                  src={tag.img}
                                  alt={tag.name}
                                />
                              ) : null}

                              <div>
                                <span className="font-bold transform transition group-hover:text-pink group-hover:translate-x-1">
                                  {tag.name}
                                </span>
                                <p className="gn-text-xs">{tag.description}</p>
                              </div>
                            </Menu.Item>
                          ))}
                          <Menu.Item
                            asChild={Link}
                            href={section.slug}
                            className="block gn-text-sm font-bold mt-xs text-center text-white bg-black p-sm rounded hover:bg-purple hover:text-white"
                            events={[
                              {
                                event: "Link Clicked",
                                properties: {
                                  title: "Show All",
                                  sourceElement: "nav",
                                  sourceName: "nav",
                                },
                              },
                            ]}
                          >
                            <span>Show All</span>
                          </Menu.Item>
                        </React.Fragment>
                      );
                    }
                    return (
                      <div key={section.slug}>
                        <h3 className="gn-text-sm font-bold uppercase mb-sm">
                          {section.heading}
                        </h3>
                        <ul className="flex flex-col space-y-sm">
                          {section.tags.map((tag) => (
                            <Menu.Item
                              asChild={Link}
                              href={tag.slug}
                              key={tag.slug}
                              className="flex items-center space-x-md gn-text-sm hover:text-pink py-xs"
                              events={[
                                {
                                  event: "Link Clicked",
                                  properties: {
                                    title: tag.name,
                                    sourceElement: "nav",
                                    sourceName: "nav",
                                  },
                                },
                              ]}
                            >
                              <div>
                                <span>{tag.name}</span>
                                <p className="gn-text-xs">{tag.description}</p>
                              </div>
                            </Menu.Item>
                          ))}
                          <Menu.Item
                            asChild={Link}
                            href={section.slug}
                            key={section.slug}
                            className="block gn-text-sm font-bold mt-xs hover:text-purple"
                            events={[
                              {
                                event: "Link Clicked",
                                properties: {
                                  title: "Show All",
                                  sourceElement: "nav",
                                  sourceName: "nav",
                                },
                              },
                            ]}
                          >
                            <span>Show All</span>
                          </Menu.Item>
                        </ul>
                      </div>
                    );
                  })}
                  {numberOfSections !== 1 ? (
                    <div className="col-span-3 -m-md mt-md flex px-md py-sm bg-gray-100 justify-between items-center">
                      <Link
                        href="/company/games"
                        className="hover:scale-150"
                        events={[
                          {
                            event: "Link Clicked",
                            properties: {
                              title: "GoNoodle Games",
                              sourceElement: "nav",
                              sourceName: "nav",
                            },
                          },
                        ]}
                      >
                        <span className="sr-only">GoNoodle Games!</span>
                        <img
                          className="inline-block mr-xs transform transition hover:scale-110 hover:-translate-y-xs"
                          src="https://cdn.gonoodle.com/Universe-Component-Library/general/logo-gn-games.svg"
                          alt="GoNoodle Games Logo"
                        />
                      </Link>
                      <p className="gn-text-sm font-bold">
                        Games that get kids moving!
                      </p>
                      <Link
                        href="/company/games"
                        className="text-black bg-white ring-offset-transparent ring-pink-300  flex items-center justify-center hover:text-pink focus:ring focus:text-pink text-xs h-button-sm px-sm font-bold rounded transition-colors focus:outline-none"
                        events={[
                          {
                            event: "Link Clicked",
                            properties: {
                              title: "GoNoodle Games",
                              sourceElement: "nav",
                              sourceName: "nav",
                            },
                          },
                        ]}
                      >
                        Learn More
                      </Link>
                    </div>
                  ) : null}
                </Menu.List>
              </Menu.Root>
            );
          })}
          <ExternalLink
            className="text-transparent bg-clip-text bg-gradient-to-r from-yellow to-pink gn-text-sm font-bold rounded border-2 border-black px-sm py-[7px] hover:border-mint focus:outline-none"
            href="https://gonoodlelive.com/"
            events={[
              {
                event: "Link Clicked",
                properties: {
                  title: "GoNoodle Live!",
                  sourceElement: "nav",
                  sourceName: "nav",
                },
              },
            ]}
            linkParams={{
              title: "GoNoodle Live!",
              sourceElement: "nav",
              sourceName: "nav",
            }}
          >
            GoNoodle Live!
          </ExternalLink>
        </div>
        {/* Desktop Nav End */}

        <div className="flex items-center justify-center">
          {user.isLoggedIn === false ? (
            user.isUserRegistrationEnabled ? (
              <>
                <Button
                  mode="light"
                  type="button"
                  size="sm"
                  label="Create an Account"
                  onClick={() => router.push("/registration")}
                  className="mx-xs font-regular"
                />
                <Button
                  mode="light-outline"
                  type="button"
                  size="sm"
                  label="Log In"
                  onClick={() => router.push("/login")}
                  className="mx-xs font-regular"
                />
              </>
            ) : (
              <Button
                mode="light"
                type="button"
                size="sm"
                label="Educator Log In"
                onClick={() => router.push(URLS.EDUCATOR_URL)}
                className="mx-xs"
              />
            )
          ) : (
            <Menu.Root>
              <Menu.Trigger className="block lg:hidden">
                {(isOpen) => {
                  if (isOpen) {
                    return (
                      <div className="h-9 w-9 text-white hover:text-mint flex items-center">
                        <span className="sr-only">Close profile menu</span>
                        <XIcon
                          className="block h-7 w-7 text-center"
                          aria-hidden="true"
                        />
                      </div>
                    );
                  }
                  return (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 610 610"
                      className="h-9 w-9 rounded-full border border-gray-300 hover:border-mint"
                    >
                      <path
                        d="M609.89 1.25q0 312.95.11 625.9c0 4-.79 4.85-4.84 4.85q-302-.19-604.07-.11Q1.09 319.62 1 7.34c0-5 1.18-6.21 6.21-6.21q301.32.21 602.68.12Zm-312.46 325.3 1.13-.87c1.37-3.44 4.32-5.8 6.09-8.94 1.86-3.29 3.22-3 5.49-.18 12.2 15.23 28.77 23.26 47.23 28 .34.32.64.82 1 .94 20.36 5.86 40.08 4.88 58.63-6.08s28.8-28.43 35.66-48.17c16.89-48.66-.13-100.49-42.13-128-6.77-4.42-14-8.38-22.49-8.37-17.94-4.82-35.68-4.77-52.85 3s-30.42 19.92-38.93 37c-12.34-12.24-23.06-18.65-38.59-23.59a104.61 104.61 0 0 0-28.78-5 63.8 63.8 0 0 0-26.56 5.46c-2.94 1.27-7.07.06-8.87 2.53-4 5.47-10.49 6.79-15.39 10.63-8.37 5.75-14.73 13.46-20 21.91-23.24 37.06-21.47 74.37-.76 111.83 2.18 3.93 4.83 5.43 9.27 6 22.87 2.85 43.84 10.5 59.17 28.49 5.07 5.94 9.73 7.42 16.88 6.44 18.78-2.58 35-9.9 47.87-24.05 2.9-2.57 5.85-5.1 7-9Zm12.64 148.71a183.37 183.37 0 0 0 33.13-3c36.92-6.38 62.28-28 79.56-60.17 5.93-11 5.51-21.89-2.29-32.24-4.85-6.45-12.16-9.68-20-8a35.47 35.47 0 0 0-14.28 6.79c-7.55 5.75-13.92 12.85-21.2 18.87-9.51 7.88-18.38 17-31.55 18.91-27.8 4.06-52.84-4.21-76.67-17.37-6.66-3.67-12.48-8.87-19.71-11.57-9.17-3.43-18.14-3.2-25.79 3.48-7.88 6.87-11.51 15.81-9.79 26.32 2 12.3 9.93 20.93 19.49 27.8 27.13 19.46 57.18 30.66 89.1 30.18Zm90.16-407.11c-7.26.39-14.9.81-21.87 4.5-2.43 1.28-4.65 2.89-4.19 6.08.43 2.9 2.84 3.84 5.27 4.64a15.46 15.46 0 0 0 5.42.55c4.65-.15 9.35-.41 13.9.67 12 2.83 24.06 4.78 33.84 14 4.42 4.14 11.81 5.38 18.28 6.22 4.28.55 8.55-.92 10.34-5.34s-1.41-7.23-4.05-10.35c-6.17-7.33-14.94-10-23.18-13.65a87 87 0 0 0-33.76-7.32ZM141.05 503.92c-.55-11.21-4.79-21.27-15.18-27.92-8.58-5.48-18.33-1.87-21.65 7.75-4.21 12.15 1.56 29.78 12.09 37s21.83 2.84 24.27-9.57a57.3 57.3 0 0 0 .47-7.26Zm19.95 56.6c.1-8.64-4.15-16.63-11.52-23-8.93-7.73-20.53-5.34-23.15 5.07-3.69 14.68 5.62 30.6 17.82 34.38 6.87 2.13 12.46-.36 15.6-6.82 1.25-2.7 1.01-5.49 1.25-9.63Zm52.74-484.44c-8.71-.15-16.53 3.13-24.55 5.92-5.5 1.91-9.2 5.69-12.61 9.86a7.91 7.91 0 0 0-.32 10.18c2.52 3.55 6 3.45 9.85 2.64 5-1.07 9.1-3.83 13.56-6.25a40.9 40.9 0 0 1 30.92-3.77c3 .82 6.53 1.45 8.38-2.16 2-3.81-.08-6.75-2.95-9.11-6.47-5.31-14.11-7.14-22.28-7.31Zm-37.9 445.67c-.07-9.6-5.88-18.81-13.77-21.83a9.63 9.63 0 0 0-13.27 7.28c-1.32 7.28.52 13.82 4.62 19.75s10.64 8.57 15.81 6.88c4.55-1.47 6.66-5.33 6.61-12.08Z"
                        fill="#7257c8"
                      />
                      <path
                        d="M609.89 1.25q-301.35 0-602.71-.12C2.15 1.13 1 2.32 1 7.34q.21 312.27.12 624.55c-.31-.22-1 .5-1-.5.05-1.33 0-2.66 0-4Q.09 316.08 0 4.75C0 1 .84.16 4.58.16Q305 .33 605.4.32c1.46 0 3.38-1.15 4.49.93Z"
                        fill="#fefefe"
                      />
                      <path
                        d="M178.11 184.9c4.9-3.84 11.4-5.16 15.39-10.63 1.8-2.47 5.93-1.26 8.87-2.53a63.8 63.8 0 0 1 26.56-5.46 104.61 104.61 0 0 1 28.78 5c15.53 4.94 26.25 11.35 38.59 23.59 8.51-17.09 21.73-29.27 38.93-37s34.91-7.77 52.85-3c3.52 1.67 7.14 3.17 10.55 5.05 35.08 19.4 50.71 49.95 51.75 89.23.27 10.16-3.8 19.91-1.89 30.1.16.89-.2 2.55-.74 2.77-3.66 1.47-3.19 5.09-4.21 7.8a110.6 110.6 0 0 1-7.11 15.86c-13.43 23.61-33 37.53-60.74 39-6.16.33-12.22-1-18.32-.19-18.46-4.77-35-12.8-47.23-28-2.27-2.82-3.63-3.11-5.49.18-1.77 3.14-4.72 5.5-6.09 8.94-.73-.17-1 .3-1.13.87l.08-.08-8.1 8c-14.18 11.22-29.62 18.91-48.62 16.95.18 2 1.53 3.34.82 5-7.65-.81-7.57-.85-11-6.85a52.17 52.17 0 0 0-5.07-7.35c-14.24-17.07-33.64-24.1-54.72-27.47-4.37-.7-8.07-.89-8.31-6.5-.05-1.19-1.21-2.32-1.83-3.49-7.21-13.69-11.68-28.2-11.9-43.87a102.64 102.64 0 0 1 6-37.58c2.21-6 3.45-12.91 7.27-17.69 4.56-5.7 8.79-11.89 14.43-16.75 1.14-.98 2.84-1.87 1.63-3.9Zm139.5 57.62c.16 1.3.26 2.61.49 3.9 3.33 18.48 2.16 36.62-4.87 54.1-1.64 4.1-1.47 6.81 2.24 9.14 7.62 9.36 17.43 15.78 28.34 20.53 27.72 12.06 57.78 6.31 75.5-15.09 30.31-36.6 33.4-95.84-6.51-134.39-19.11-18.46-41.9-25.83-67.68-18.52-19.89 5.63-32.79 20.56-43.12 37.88-2.06 3.45-2.17 5.84.29 9.21a96.5 96.5 0 0 1 15.32 33.24Zm-160.25 8.32c.17 23 4.86 39.2 13.24 54.47 1.86 3.38 4.13 4.77 8 5.35 22.54 3.4 42.49 11.84 56.43 30.95 1.36 1.86 2.86 1.9 4.69 1.89 23.41 0 42.29-9.64 56.22-28.08 18.92-25.08 22.37-53.25 12.81-82.63-7.52-23.1-20.51-42.54-44.19-52.26-22.71-9.31-45.85-11.28-67.45 2.07-27.03 16.7-38.53 42.67-39.75 68.24Z"
                        fill="#080809"
                      />
                      <path
                        d="M310.07 475.26c-31.92.48-62-10.72-89.11-30.22-9.56-6.87-17.49-15.5-19.49-27.8-1.72-10.51 1.91-19.45 9.79-26.32 7.65-6.68 16.62-6.91 25.79-3.48 7.23 2.7 13 7.9 19.71 11.57 23.83 13.16 48.87 21.43 76.67 17.37 13.17-1.93 22-11 31.55-18.91 7.28-6 13.65-13.12 21.2-18.87a35.47 35.47 0 0 1 14.28-6.79c7.85-1.66 15.16 1.57 20 8 7.8 10.35 8.22 21.21 2.29 32.24-17.28 32.18-42.64 53.79-79.56 60.17a183.37 183.37 0 0 1-33.12 3.04Zm2.21-7c41.46-.29 73.47-13.61 98-43.23 5.11-6.17 9.6-12.73 11.59-20.66 2.76-11-2.56-21.26-12.55-23.62-6.49-1.53-13.06-1.06-18.66 2.94a159.44 159.44 0 0 0-23.84 21.07c-6.67 7.15-14.12 12.53-23.3 15.38-25.79 8-50.51 2.84-74.79-6.63-8.58-3.35-15.86-9-23.65-13.75-8.49-5.17-17.34-7.53-27-3.26-5.3 2.34-8.76 6.3-8.8 12.18-.06 9.42 3.41 17.87 10.48 23.9 27.62 23.5 59.88 34.89 92.52 35.68Z"
                        fill="#09050b"
                      />
                      <path
                        fill="#f6f5f7"
                        d="M178.11 184.9c1.21 2-.49 2.92-1.63 3.9-5.64 4.86-9.87 11-14.43 16.75-3.82 4.78-5.06 11.67-7.27 17.69a102.64 102.64 0 0 0-6 37.58c.26 15.67 4.69 30.16 11.88 43.93.62 1.17 1.78 2.3 1.83 3.49.24 5.61 3.94 5.8 8.31 6.5 21.08 3.37 40.48 10.4 54.72 27.47a52.17 52.17 0 0 1 5.07 7.35c3.45 6 3.37 6 11 6.85.71-1.64-.64-3-.82-5 19 2 34.44-5.73 48.62-16.95l1.1 1c-12.88 14.15-29.09 21.47-47.87 24.05-7.15 1-11.81-.5-16.88-6.44-15.33-18-36.3-25.64-59.17-28.49-4.44-.55-7.09-2.05-9.27-6-20.71-37.46-22.48-74.77.76-111.83 5.32-8.39 11.68-16.1 20.05-21.85ZM357.37 344.59c6.1-.82 12.16.52 18.32.19 27.77-1.49 47.31-15.41 60.74-39a110.6 110.6 0 0 0 7.11-15.86c1-2.71.55-6.33 4.21-7.8.54-.22.9-1.88.74-2.77-1.91-10.19 2.16-19.94 1.89-30.1-1-39.28-16.67-69.83-51.75-89.23-3.41-1.88-7-3.38-10.55-5.05 8.53 0 15.72 4 22.49 8.37 42 27.47 59 79.3 42.13 128-6.86 19.66-17.06 37.1-35.7 48.11s-38.27 11.94-58.63 6.08c-.37-.12-.66-.62-1-.94Z"
                      />
                      <path
                        fill="#030206"
                        d="M400.23 68.15A87 87 0 0 1 434 75.44c8.24 3.61 17 6.32 23.18 13.65 2.64 3.12 5.84 6 4.05 10.35s-6.06 5.89-10.34 5.34c-6.47-.84-13.86-2.08-18.28-6.22-9.78-9.19-21.85-11.14-33.84-14-4.55-1.08-9.25-.82-13.9-.67a15.46 15.46 0 0 1-5.42-.55c-2.43-.8-4.84-1.74-5.27-4.64-.46-3.19 1.76-4.8 4.19-6.08 6.96-3.62 14.63-4.08 21.86-4.47Z"
                      />
                      <path
                        fill="#372372"
                        d="M141.05 503.92a57.3 57.3 0 0 1-.47 7.22c-2.44 12.41-13.68 16.8-24.27 9.57s-16.3-24.81-12.09-37c3.32-9.62 13.07-13.23 21.65-7.75 10.39 6.69 14.63 16.75 15.18 27.96ZM161 560.52c-.22 4.14.06 6.93-1.25 9.63-3.14 6.46-8.73 9-15.6 6.82-12.2-3.78-21.51-19.7-17.82-34.38 2.62-10.41 14.22-12.8 23.15-5.07 7.35 6.37 11.6 14.36 11.52 23Z"
                      />
                      <path
                        fill="#030206"
                        d="M213.74 76.08c8.17.17 15.81 2 22.28 7.31 2.87 2.36 4.91 5.3 2.95 9.11-1.85 3.61-5.36 3-8.38 2.16a40.9 40.9 0 0 0-30.92 3.77c-4.46 2.42-8.51 5.18-13.56 6.25-3.82.81-7.33.91-9.85-2.64a7.91 7.91 0 0 1 .32-10.18c3.41-4.17 7.11-8 12.61-9.86 8.02-2.79 15.81-6.07 24.55-5.92Z"
                      />
                      <path
                        fill="#372372"
                        d="M175.84 521.75c0 6.75-2.06 10.61-6.61 12.08-5.17 1.69-11.78-1.05-15.81-6.88s-5.94-12.47-4.62-19.75a9.63 9.63 0 0 1 13.27-7.28c7.93 3.02 13.7 12.23 13.77 21.83Z"
                      />
                      <path
                        fill="#f6f5f7"
                        d="m290.51 335.5-1.1-1 8.1-8c-1.15 3.9-4.1 6.43-7 9ZM297.43 326.55c.16-.57.4-1 1.13-.87Z"
                      />
                      <path
                        d="M157.36 250.84c1.22-25.57 12.72-51.54 39.76-68.24 21.6-13.35 44.74-11.38 67.45-2.07 23.68 9.72 36.67 29.16 44.19 52.26 9.56 29.38 6.11 57.55-12.81 82.63-13.93 18.44-32.81 28.05-56.22 28.08-1.83 0-3.33 0-4.69-1.89-13.94-19.11-33.89-27.55-56.43-30.95-3.88-.58-6.15-2-8-5.35-8.39-15.31-13.08-31.49-13.25-54.47Zm61-10.55v.18c-.44.19-1 .25-1.29.57-12.21 14.5-12.24 40.73-.1 55.21 7.69 9.16 19.07 12.32 30.06 8.36 13-4.67 24.1-20.21 24.44-34.15a90.51 90.51 0 0 0 2.69-9.55c2.33-13.45.93-26.08-9.46-36.16-8.07-7.84-20.22-8.61-29.83-2.88-7.16 4.28-11.36 10.9-15.77 17.48-.61.07-.91.34-.77.94Z"
                        fill="#fcfcfd"
                      />
                      <path
                        d="M317.61 242.52a96.5 96.5 0 0 0-15.32-33.24c-2.46-3.37-2.35-5.76-.29-9.21 10.33-17.32 23.23-32.25 43.12-37.88 25.78-7.31 48.57.06 67.68 18.52 39.91 38.55 36.82 97.79 6.51 134.39-17.72 21.4-47.78 27.15-75.5 15.09-10.91-4.75-20.72-11.17-28.34-20.53 4.17-9.38 5.77-19.37 6.55-29.46.98-12.8-.02-25.46-4.41-37.68Zm84.19 8.78c.65-7-1.65-14.69-5.71-21.9-9.7-17.25-30.09-20.06-44-6.06s-15.73 39.9-3.66 56.45c10.46 14.34 30.12 15.6 42.33 2.72 7.93-8.39 11.24-18.51 11.04-31.21Z"
                        fill="#faf9fb"
                      />
                      <path
                        fill="#6f55c3"
                        d="M317.61 242.52C322 254.74 323 267.4 322 280.2c-.78 10.09-2.38 20.08-6.55 29.46-3.71-2.33-3.88-5-2.24-9.14 7-17.48 8.2-35.62 4.87-54.1-.21-1.29-.31-2.6-.47-3.9Z"
                      />
                      <path
                        d="M312.28 468.26c-32.64-.79-64.9-12.18-92.49-35.68-7.07-6-10.54-14.48-10.48-23.9 0-5.88 3.5-9.84 8.8-12.18 9.64-4.27 18.49-1.91 27 3.26 7.79 4.75 15.07 10.4 23.65 13.75 24.28 9.47 49 14.64 74.79 6.63 9.18-2.85 16.63-8.23 23.3-15.38a159.44 159.44 0 0 1 23.84-21.07c5.6-4 12.17-4.47 18.66-2.94 10 2.36 15.31 12.65 12.55 23.62-2 7.93-6.48 14.49-11.59 20.66-24.56 29.62-56.57 42.97-98.03 43.23Zm94.16-78.9c-2.81.61-3.19 2.45-4 3.78-23.29 37.28-66.5 54.52-108.77 48.63a113.75 113.75 0 0 1-64.73-31.39c-1.63-1.57-3.36-3.15-5.46-.85-1.89 2.07-.84 3.94.78 5.67a107.53 107.53 0 0 0 13.14 12.19 117.64 117.64 0 0 0 58.49 23.3c28.11 3.31 54.72-1.06 78.37-17.53 15.38-10.73 26.02-25.23 32.18-43.8Z"
                        fill="#d35096"
                      />
                      <path
                        d="M219.05 239.35c4.41-6.58 8.61-13.2 15.77-17.48 9.61-5.73 21.76-5 29.83 2.88 10.39 10.08 11.79 22.71 9.46 36.16a90.51 90.51 0 0 1-2.69 9.55c-2.16 3.11-4.11 6.4-6.53 9.29-12.15 14.57-31.79 14.62-42.07.13-5.49-7.74-7.19-16.51-6.71-25.89.23-4.62 2.46-8.85 2.2-13.52v-.18c1.02.27 1.04-.21.74-.94Zm8.34 3.37c-.06 5.45 2 9.91 5.37 11.36a8.47 8.47 0 0 0 8.92-.92c4.29-3.28 6.88-10.45 5.46-15.62-1.17-4.24-3.38-7.84-8.12-8.33s-7.69 2.34-9.85 6.24a14.16 14.16 0 0 0-1.78 7.27Z"
                        fill="#030204"
                      />
                      <path
                        fill="#7359c6"
                        d="M218.31 240.47c.26 4.67-2 8.9-2.2 13.52-.48 9.38 1.22 18.15 6.71 25.89 10.28 14.49 29.92 14.44 42.07-.13 2.42-2.89 4.37-6.18 6.53-9.29-.34 13.94-11.42 29.48-24.42 34.15-11 4-22.37.8-30.06-8.36-12.14-14.48-12.11-40.71.1-55.21.25-.32.83-.38 1.27-.57ZM219.05 239.35c.3.73.28 1.21-.72.94-.14-.6.16-.87.72-.94Z"
                      />
                      <path
                        d="M401.8 251.3c.2 12.73-3.11 22.82-11.06 31.21-12.21 12.88-31.87 11.62-42.33-2.72-12.07-16.55-10.39-42.32 3.66-56.45s34.32-11.19 44 6.06c4.08 7.21 6.38 14.86 5.73 21.9Zm-31.16-9.71c-.11-2.47-.22-4.92-1.37-7.23-1.74-3.48-4.3-6.07-8.26-6.29-4.21-.24-7.16 2.31-9.09 5.82a16.31 16.31 0 0 0-.79 13.91c1.52 4.16 4.3 7.25 9.08 7.12 4.56-.13 7.59-2.82 9.27-7a17.58 17.58 0 0 0 1.16-6.33Z"
                        fill="#040307"
                      />
                      <path
                        d="M406.44 389.36c-6.16 18.57-16.8 33.07-32.2 43.8-23.65 16.47-50.24 20.84-78.37 17.53a117.64 117.64 0 0 1-58.49-23.3 107.53 107.53 0 0 1-13.14-12.19c-1.62-1.73-2.67-3.6-.78-5.67 2.1-2.3 3.83-.72 5.46.85a113.75 113.75 0 0 0 64.73 31.39c42.27 5.89 85.48-11.35 108.77-48.63.83-1.33 1.21-3.14 4.02-3.78Z"
                        fill="#0b0408"
                      />
                      <path
                        fill="#6f55c3"
                        d="M227.39 242.72a14.16 14.16 0 0 1 1.78-7.27c2.16-3.9 5.19-6.72 9.85-6.24s6.95 4.09 8.12 8.33c1.42 5.17-1.17 12.34-5.46 15.62a8.47 8.47 0 0 1-8.92.92c-3.41-1.45-5.43-5.91-5.37-11.36ZM370.63 241.59a17.36 17.36 0 0 1-1.15 6.3c-1.68 4.21-4.71 6.9-9.27 7-4.78.13-7.56-3-9.08-7.12a16.31 16.31 0 0 1 .79-13.91c1.93-3.51 4.88-6.06 9.09-5.82 4 .22 6.52 2.81 8.26 6.29a17.19 17.19 0 0 1 1.36 7.26Z"
                      />
                    </svg>
                  );
                }}
              </Menu.Trigger>

              <Menu.Trigger className="hidden lg:block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 610 610"
                  className="h-9 w-9 rounded-full border border-gray-300 hover:border-mint"
                >
                  <path
                    d="M609.89 1.25q0 312.95.11 625.9c0 4-.79 4.85-4.84 4.85q-302-.19-604.07-.11Q1.09 319.62 1 7.34c0-5 1.18-6.21 6.21-6.21q301.32.21 602.68.12Zm-312.46 325.3 1.13-.87c1.37-3.44 4.32-5.8 6.09-8.94 1.86-3.29 3.22-3 5.49-.18 12.2 15.23 28.77 23.26 47.23 28 .34.32.64.82 1 .94 20.36 5.86 40.08 4.88 58.63-6.08s28.8-28.43 35.66-48.17c16.89-48.66-.13-100.49-42.13-128-6.77-4.42-14-8.38-22.49-8.37-17.94-4.82-35.68-4.77-52.85 3s-30.42 19.92-38.93 37c-12.34-12.24-23.06-18.65-38.59-23.59a104.61 104.61 0 0 0-28.78-5 63.8 63.8 0 0 0-26.56 5.46c-2.94 1.27-7.07.06-8.87 2.53-4 5.47-10.49 6.79-15.39 10.63-8.37 5.75-14.73 13.46-20 21.91-23.24 37.06-21.47 74.37-.76 111.83 2.18 3.93 4.83 5.43 9.27 6 22.87 2.85 43.84 10.5 59.17 28.49 5.07 5.94 9.73 7.42 16.88 6.44 18.78-2.58 35-9.9 47.87-24.05 2.9-2.57 5.85-5.1 7-9Zm12.64 148.71a183.37 183.37 0 0 0 33.13-3c36.92-6.38 62.28-28 79.56-60.17 5.93-11 5.51-21.89-2.29-32.24-4.85-6.45-12.16-9.68-20-8a35.47 35.47 0 0 0-14.28 6.79c-7.55 5.75-13.92 12.85-21.2 18.87-9.51 7.88-18.38 17-31.55 18.91-27.8 4.06-52.84-4.21-76.67-17.37-6.66-3.67-12.48-8.87-19.71-11.57-9.17-3.43-18.14-3.2-25.79 3.48-7.88 6.87-11.51 15.81-9.79 26.32 2 12.3 9.93 20.93 19.49 27.8 27.13 19.46 57.18 30.66 89.1 30.18Zm90.16-407.11c-7.26.39-14.9.81-21.87 4.5-2.43 1.28-4.65 2.89-4.19 6.08.43 2.9 2.84 3.84 5.27 4.64a15.46 15.46 0 0 0 5.42.55c4.65-.15 9.35-.41 13.9.67 12 2.83 24.06 4.78 33.84 14 4.42 4.14 11.81 5.38 18.28 6.22 4.28.55 8.55-.92 10.34-5.34s-1.41-7.23-4.05-10.35c-6.17-7.33-14.94-10-23.18-13.65a87 87 0 0 0-33.76-7.32ZM141.05 503.92c-.55-11.21-4.79-21.27-15.18-27.92-8.58-5.48-18.33-1.87-21.65 7.75-4.21 12.15 1.56 29.78 12.09 37s21.83 2.84 24.27-9.57a57.3 57.3 0 0 0 .47-7.26Zm19.95 56.6c.1-8.64-4.15-16.63-11.52-23-8.93-7.73-20.53-5.34-23.15 5.07-3.69 14.68 5.62 30.6 17.82 34.38 6.87 2.13 12.46-.36 15.6-6.82 1.25-2.7 1.01-5.49 1.25-9.63Zm52.74-484.44c-8.71-.15-16.53 3.13-24.55 5.92-5.5 1.91-9.2 5.69-12.61 9.86a7.91 7.91 0 0 0-.32 10.18c2.52 3.55 6 3.45 9.85 2.64 5-1.07 9.1-3.83 13.56-6.25a40.9 40.9 0 0 1 30.92-3.77c3 .82 6.53 1.45 8.38-2.16 2-3.81-.08-6.75-2.95-9.11-6.47-5.31-14.11-7.14-22.28-7.31Zm-37.9 445.67c-.07-9.6-5.88-18.81-13.77-21.83a9.63 9.63 0 0 0-13.27 7.28c-1.32 7.28.52 13.82 4.62 19.75s10.64 8.57 15.81 6.88c4.55-1.47 6.66-5.33 6.61-12.08Z"
                    fill="#7257c8"
                  />
                  <path
                    d="M609.89 1.25q-301.35 0-602.71-.12C2.15 1.13 1 2.32 1 7.34q.21 312.27.12 624.55c-.31-.22-1 .5-1-.5.05-1.33 0-2.66 0-4Q.09 316.08 0 4.75C0 1 .84.16 4.58.16Q305 .33 605.4.32c1.46 0 3.38-1.15 4.49.93Z"
                    fill="#fefefe"
                  />
                  <path
                    d="M178.11 184.9c4.9-3.84 11.4-5.16 15.39-10.63 1.8-2.47 5.93-1.26 8.87-2.53a63.8 63.8 0 0 1 26.56-5.46 104.61 104.61 0 0 1 28.78 5c15.53 4.94 26.25 11.35 38.59 23.59 8.51-17.09 21.73-29.27 38.93-37s34.91-7.77 52.85-3c3.52 1.67 7.14 3.17 10.55 5.05 35.08 19.4 50.71 49.95 51.75 89.23.27 10.16-3.8 19.91-1.89 30.1.16.89-.2 2.55-.74 2.77-3.66 1.47-3.19 5.09-4.21 7.8a110.6 110.6 0 0 1-7.11 15.86c-13.43 23.61-33 37.53-60.74 39-6.16.33-12.22-1-18.32-.19-18.46-4.77-35-12.8-47.23-28-2.27-2.82-3.63-3.11-5.49.18-1.77 3.14-4.72 5.5-6.09 8.94-.73-.17-1 .3-1.13.87l.08-.08-8.1 8c-14.18 11.22-29.62 18.91-48.62 16.95.18 2 1.53 3.34.82 5-7.65-.81-7.57-.85-11-6.85a52.17 52.17 0 0 0-5.07-7.35c-14.24-17.07-33.64-24.1-54.72-27.47-4.37-.7-8.07-.89-8.31-6.5-.05-1.19-1.21-2.32-1.83-3.49-7.21-13.69-11.68-28.2-11.9-43.87a102.64 102.64 0 0 1 6-37.58c2.21-6 3.45-12.91 7.27-17.69 4.56-5.7 8.79-11.89 14.43-16.75 1.14-.98 2.84-1.87 1.63-3.9Zm139.5 57.62c.16 1.3.26 2.61.49 3.9 3.33 18.48 2.16 36.62-4.87 54.1-1.64 4.1-1.47 6.81 2.24 9.14 7.62 9.36 17.43 15.78 28.34 20.53 27.72 12.06 57.78 6.31 75.5-15.09 30.31-36.6 33.4-95.84-6.51-134.39-19.11-18.46-41.9-25.83-67.68-18.52-19.89 5.63-32.79 20.56-43.12 37.88-2.06 3.45-2.17 5.84.29 9.21a96.5 96.5 0 0 1 15.32 33.24Zm-160.25 8.32c.17 23 4.86 39.2 13.24 54.47 1.86 3.38 4.13 4.77 8 5.35 22.54 3.4 42.49 11.84 56.43 30.95 1.36 1.86 2.86 1.9 4.69 1.89 23.41 0 42.29-9.64 56.22-28.08 18.92-25.08 22.37-53.25 12.81-82.63-7.52-23.1-20.51-42.54-44.19-52.26-22.71-9.31-45.85-11.28-67.45 2.07-27.03 16.7-38.53 42.67-39.75 68.24Z"
                    fill="#080809"
                  />
                  <path
                    d="M310.07 475.26c-31.92.48-62-10.72-89.11-30.22-9.56-6.87-17.49-15.5-19.49-27.8-1.72-10.51 1.91-19.45 9.79-26.32 7.65-6.68 16.62-6.91 25.79-3.48 7.23 2.7 13 7.9 19.71 11.57 23.83 13.16 48.87 21.43 76.67 17.37 13.17-1.93 22-11 31.55-18.91 7.28-6 13.65-13.12 21.2-18.87a35.47 35.47 0 0 1 14.28-6.79c7.85-1.66 15.16 1.57 20 8 7.8 10.35 8.22 21.21 2.29 32.24-17.28 32.18-42.64 53.79-79.56 60.17a183.37 183.37 0 0 1-33.12 3.04Zm2.21-7c41.46-.29 73.47-13.61 98-43.23 5.11-6.17 9.6-12.73 11.59-20.66 2.76-11-2.56-21.26-12.55-23.62-6.49-1.53-13.06-1.06-18.66 2.94a159.44 159.44 0 0 0-23.84 21.07c-6.67 7.15-14.12 12.53-23.3 15.38-25.79 8-50.51 2.84-74.79-6.63-8.58-3.35-15.86-9-23.65-13.75-8.49-5.17-17.34-7.53-27-3.26-5.3 2.34-8.76 6.3-8.8 12.18-.06 9.42 3.41 17.87 10.48 23.9 27.62 23.5 59.88 34.89 92.52 35.68Z"
                    fill="#09050b"
                  />
                  <path
                    fill="#f6f5f7"
                    d="M178.11 184.9c1.21 2-.49 2.92-1.63 3.9-5.64 4.86-9.87 11-14.43 16.75-3.82 4.78-5.06 11.67-7.27 17.69a102.64 102.64 0 0 0-6 37.58c.26 15.67 4.69 30.16 11.88 43.93.62 1.17 1.78 2.3 1.83 3.49.24 5.61 3.94 5.8 8.31 6.5 21.08 3.37 40.48 10.4 54.72 27.47a52.17 52.17 0 0 1 5.07 7.35c3.45 6 3.37 6 11 6.85.71-1.64-.64-3-.82-5 19 2 34.44-5.73 48.62-16.95l1.1 1c-12.88 14.15-29.09 21.47-47.87 24.05-7.15 1-11.81-.5-16.88-6.44-15.33-18-36.3-25.64-59.17-28.49-4.44-.55-7.09-2.05-9.27-6-20.71-37.46-22.48-74.77.76-111.83 5.32-8.39 11.68-16.1 20.05-21.85ZM357.37 344.59c6.1-.82 12.16.52 18.32.19 27.77-1.49 47.31-15.41 60.74-39a110.6 110.6 0 0 0 7.11-15.86c1-2.71.55-6.33 4.21-7.8.54-.22.9-1.88.74-2.77-1.91-10.19 2.16-19.94 1.89-30.1-1-39.28-16.67-69.83-51.75-89.23-3.41-1.88-7-3.38-10.55-5.05 8.53 0 15.72 4 22.49 8.37 42 27.47 59 79.3 42.13 128-6.86 19.66-17.06 37.1-35.7 48.11s-38.27 11.94-58.63 6.08c-.37-.12-.66-.62-1-.94Z"
                  />
                  <path
                    fill="#030206"
                    d="M400.23 68.15A87 87 0 0 1 434 75.44c8.24 3.61 17 6.32 23.18 13.65 2.64 3.12 5.84 6 4.05 10.35s-6.06 5.89-10.34 5.34c-6.47-.84-13.86-2.08-18.28-6.22-9.78-9.19-21.85-11.14-33.84-14-4.55-1.08-9.25-.82-13.9-.67a15.46 15.46 0 0 1-5.42-.55c-2.43-.8-4.84-1.74-5.27-4.64-.46-3.19 1.76-4.8 4.19-6.08 6.96-3.62 14.63-4.08 21.86-4.47Z"
                  />
                  <path
                    fill="#372372"
                    d="M141.05 503.92a57.3 57.3 0 0 1-.47 7.22c-2.44 12.41-13.68 16.8-24.27 9.57s-16.3-24.81-12.09-37c3.32-9.62 13.07-13.23 21.65-7.75 10.39 6.69 14.63 16.75 15.18 27.96ZM161 560.52c-.22 4.14.06 6.93-1.25 9.63-3.14 6.46-8.73 9-15.6 6.82-12.2-3.78-21.51-19.7-17.82-34.38 2.62-10.41 14.22-12.8 23.15-5.07 7.35 6.37 11.6 14.36 11.52 23Z"
                  />
                  <path
                    fill="#030206"
                    d="M213.74 76.08c8.17.17 15.81 2 22.28 7.31 2.87 2.36 4.91 5.3 2.95 9.11-1.85 3.61-5.36 3-8.38 2.16a40.9 40.9 0 0 0-30.92 3.77c-4.46 2.42-8.51 5.18-13.56 6.25-3.82.81-7.33.91-9.85-2.64a7.91 7.91 0 0 1 .32-10.18c3.41-4.17 7.11-8 12.61-9.86 8.02-2.79 15.81-6.07 24.55-5.92Z"
                  />
                  <path
                    fill="#372372"
                    d="M175.84 521.75c0 6.75-2.06 10.61-6.61 12.08-5.17 1.69-11.78-1.05-15.81-6.88s-5.94-12.47-4.62-19.75a9.63 9.63 0 0 1 13.27-7.28c7.93 3.02 13.7 12.23 13.77 21.83Z"
                  />
                  <path
                    fill="#f6f5f7"
                    d="m290.51 335.5-1.1-1 8.1-8c-1.15 3.9-4.1 6.43-7 9ZM297.43 326.55c.16-.57.4-1 1.13-.87Z"
                  />
                  <path
                    d="M157.36 250.84c1.22-25.57 12.72-51.54 39.76-68.24 21.6-13.35 44.74-11.38 67.45-2.07 23.68 9.72 36.67 29.16 44.19 52.26 9.56 29.38 6.11 57.55-12.81 82.63-13.93 18.44-32.81 28.05-56.22 28.08-1.83 0-3.33 0-4.69-1.89-13.94-19.11-33.89-27.55-56.43-30.95-3.88-.58-6.15-2-8-5.35-8.39-15.31-13.08-31.49-13.25-54.47Zm61-10.55v.18c-.44.19-1 .25-1.29.57-12.21 14.5-12.24 40.73-.1 55.21 7.69 9.16 19.07 12.32 30.06 8.36 13-4.67 24.1-20.21 24.44-34.15a90.51 90.51 0 0 0 2.69-9.55c2.33-13.45.93-26.08-9.46-36.16-8.07-7.84-20.22-8.61-29.83-2.88-7.16 4.28-11.36 10.9-15.77 17.48-.61.07-.91.34-.77.94Z"
                    fill="#fcfcfd"
                  />
                  <path
                    d="M317.61 242.52a96.5 96.5 0 0 0-15.32-33.24c-2.46-3.37-2.35-5.76-.29-9.21 10.33-17.32 23.23-32.25 43.12-37.88 25.78-7.31 48.57.06 67.68 18.52 39.91 38.55 36.82 97.79 6.51 134.39-17.72 21.4-47.78 27.15-75.5 15.09-10.91-4.75-20.72-11.17-28.34-20.53 4.17-9.38 5.77-19.37 6.55-29.46.98-12.8-.02-25.46-4.41-37.68Zm84.19 8.78c.65-7-1.65-14.69-5.71-21.9-9.7-17.25-30.09-20.06-44-6.06s-15.73 39.9-3.66 56.45c10.46 14.34 30.12 15.6 42.33 2.72 7.93-8.39 11.24-18.51 11.04-31.21Z"
                    fill="#faf9fb"
                  />
                  <path
                    fill="#6f55c3"
                    d="M317.61 242.52C322 254.74 323 267.4 322 280.2c-.78 10.09-2.38 20.08-6.55 29.46-3.71-2.33-3.88-5-2.24-9.14 7-17.48 8.2-35.62 4.87-54.1-.21-1.29-.31-2.6-.47-3.9Z"
                  />
                  <path
                    d="M312.28 468.26c-32.64-.79-64.9-12.18-92.49-35.68-7.07-6-10.54-14.48-10.48-23.9 0-5.88 3.5-9.84 8.8-12.18 9.64-4.27 18.49-1.91 27 3.26 7.79 4.75 15.07 10.4 23.65 13.75 24.28 9.47 49 14.64 74.79 6.63 9.18-2.85 16.63-8.23 23.3-15.38a159.44 159.44 0 0 1 23.84-21.07c5.6-4 12.17-4.47 18.66-2.94 10 2.36 15.31 12.65 12.55 23.62-2 7.93-6.48 14.49-11.59 20.66-24.56 29.62-56.57 42.97-98.03 43.23Zm94.16-78.9c-2.81.61-3.19 2.45-4 3.78-23.29 37.28-66.5 54.52-108.77 48.63a113.75 113.75 0 0 1-64.73-31.39c-1.63-1.57-3.36-3.15-5.46-.85-1.89 2.07-.84 3.94.78 5.67a107.53 107.53 0 0 0 13.14 12.19 117.64 117.64 0 0 0 58.49 23.3c28.11 3.31 54.72-1.06 78.37-17.53 15.38-10.73 26.02-25.23 32.18-43.8Z"
                    fill="#d35096"
                  />
                  <path
                    d="M219.05 239.35c4.41-6.58 8.61-13.2 15.77-17.48 9.61-5.73 21.76-5 29.83 2.88 10.39 10.08 11.79 22.71 9.46 36.16a90.51 90.51 0 0 1-2.69 9.55c-2.16 3.11-4.11 6.4-6.53 9.29-12.15 14.57-31.79 14.62-42.07.13-5.49-7.74-7.19-16.51-6.71-25.89.23-4.62 2.46-8.85 2.2-13.52v-.18c1.02.27 1.04-.21.74-.94Zm8.34 3.37c-.06 5.45 2 9.91 5.37 11.36a8.47 8.47 0 0 0 8.92-.92c4.29-3.28 6.88-10.45 5.46-15.62-1.17-4.24-3.38-7.84-8.12-8.33s-7.69 2.34-9.85 6.24a14.16 14.16 0 0 0-1.78 7.27Z"
                    fill="#030204"
                  />
                  <path
                    fill="#7359c6"
                    d="M218.31 240.47c.26 4.67-2 8.9-2.2 13.52-.48 9.38 1.22 18.15 6.71 25.89 10.28 14.49 29.92 14.44 42.07-.13 2.42-2.89 4.37-6.18 6.53-9.29-.34 13.94-11.42 29.48-24.42 34.15-11 4-22.37.8-30.06-8.36-12.14-14.48-12.11-40.71.1-55.21.25-.32.83-.38 1.27-.57ZM219.05 239.35c.3.73.28 1.21-.72.94-.14-.6.16-.87.72-.94Z"
                  />
                  <path
                    d="M401.8 251.3c.2 12.73-3.11 22.82-11.06 31.21-12.21 12.88-31.87 11.62-42.33-2.72-12.07-16.55-10.39-42.32 3.66-56.45s34.32-11.19 44 6.06c4.08 7.21 6.38 14.86 5.73 21.9Zm-31.16-9.71c-.11-2.47-.22-4.92-1.37-7.23-1.74-3.48-4.3-6.07-8.26-6.29-4.21-.24-7.16 2.31-9.09 5.82a16.31 16.31 0 0 0-.79 13.91c1.52 4.16 4.3 7.25 9.08 7.12 4.56-.13 7.59-2.82 9.27-7a17.58 17.58 0 0 0 1.16-6.33Z"
                    fill="#040307"
                  />
                  <path
                    d="M406.44 389.36c-6.16 18.57-16.8 33.07-32.2 43.8-23.65 16.47-50.24 20.84-78.37 17.53a117.64 117.64 0 0 1-58.49-23.3 107.53 107.53 0 0 1-13.14-12.19c-1.62-1.73-2.67-3.6-.78-5.67 2.1-2.3 3.83-.72 5.46.85a113.75 113.75 0 0 0 64.73 31.39c42.27 5.89 85.48-11.35 108.77-48.63.83-1.33 1.21-3.14 4.02-3.78Z"
                    fill="#0b0408"
                  />
                  <path
                    fill="#6f55c3"
                    d="M227.39 242.72a14.16 14.16 0 0 1 1.78-7.27c2.16-3.9 5.19-6.72 9.85-6.24s6.95 4.09 8.12 8.33c1.42 5.17-1.17 12.34-5.46 15.62a8.47 8.47 0 0 1-8.92.92c-3.41-1.45-5.43-5.91-5.37-11.36ZM370.63 241.59a17.36 17.36 0 0 1-1.15 6.3c-1.68 4.21-4.71 6.9-9.27 7-4.78.13-7.56-3-9.08-7.12a16.31 16.31 0 0 1 .79-13.91c1.93-3.51 4.88-6.06 9.09-5.82 4 .22 6.52 2.81 8.26 6.29a17.19 17.19 0 0 1 1.36 7.26Z"
                  />
                </svg>
              </Menu.Trigger>

              <Menu.List
                asChild="div"
                className="flex flex-col right-0 left-0 top-[72px] lg:right-7 lg:left-auto lg:rounded overflow-hidden"
              >
                <div
                  style={{
                    backgroundImage: `url(${profileMenuBackground.src})`,
                  }}
                  className="bg-no-repeat bg-auto bg-right-bottom bg-purple-900 min-w-[300px] min-h-[160px]"
                >
                  <div className="px-7 py-8 text-white">
                    <h2 className="font-bold text-lg leading-7 max-w-60 text-ellipsis overflow-hidden">
                      {profile.name}
                    </h2>
                    <p className="gn-text-sm max-w-48 text-ellipsis overflow-hidden">
                      {profile.classroomType === PROFILE_TYPES.CLASSROOM
                        ? profile.school.name
                        : profile.gradeName}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col bg-white space-y-xs px-4 py-3 gn-text">
                  <Menu.Item
                    asChild={Link}
                    href={`/${ROUTE_PREFIX.PROFILES}`}
                    className="hover:bg-gray-100 rounded p-sm"
                  >
                    My Profiles
                  </Menu.Item>
                  <Menu.Item
                    asChild={Link}
                    href={`/${ROUTE_PREFIX.ACCOUNT_SETTINGS}`}
                    className="hover:bg-gray-100 rounded p-sm"
                  >
                    Account Settings
                  </Menu.Item>
                  <Menu.Item
                    asChild={Link}
                    href="https://support.gonoodle.com/"
                    className="hover:bg-gray-100 rounded p-sm"
                  >
                    Help Center
                  </Menu.Item>
                  <hr className="-mx-6" />
                  <div className="hover:bg-gray-100 rounded p-sm">
                    <button
                      className="w-full text-left"
                      type="button"
                      onClick={() => logout()}
                    >
                      Log Out
                    </button>
                  </div>
                </div>
              </Menu.List>
            </Menu.Root>
          )}
          <Link href="/search" className="ml-4 flex-shrink-0">
            <button
              type="button"
              className="text-white hover:text-mint p-xs focus:outline-none"
            >
              <span className="sr-only">Search</span>
              <SearchIcon className="h-7 w-7" aria-hidden="true" />
            </button>
          </Link>
        </div>
      </div>
    </nav>
  );
}
